import { useEffect, useState, useMemo } from "react";
import { useBackState } from "@/hooks/useScrollMemory";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SyntheticDataBanner } from "@/components/SyntheticDataBanner";
import { TagNamingReport } from "@/components/catchup/TagNamingReport";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, DollarSign, PiggyBank, Wallet, Lock, Info, Clock,
  Layers, Boxes, Tag, ExternalLink, Gauge, AlertTriangle, Activity, Download,
  CheckCircle2, Camera, HelpCircle, RotateCcw, Trash2, MapPin, Archive, ChevronDown, ChevronUp, FlaskConical, Wrench, Sparkles, Recycle,
} from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const FIN_WINDOWS = [[90, "90d"], [180, "180d"], [365, "365d"]];
const OPS_WINDOWS = [[7, "This week"], [30, "30 days"], [90, "Quarter"], [180, "180 days"], [365, "Year"]];

const Coverage = ({ label, testid }) => label ? (
  <div data-testid={testid} className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-1.5 font-mono">
    <Info className="w-3 h-3 shrink-0" /> {label}
  </div>
) : null;

const Card = ({ title, icon: Icon, children, testid, accent = "text-slate-400", right }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon className={`w-4 h-4 ${accent}`} />}
        <h3 className="font-display font-bold text-sm uppercase tracking-wide text-slate-700">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </div>
);

const PartLinks = ({ items, testid, empty = "Nothing here — nice." }) => {
  const [open, setOpen] = useState(false);
  if (!items?.length) return <p className="text-sm text-slate-400">{empty}</p>;
  const shown = open ? items : items.slice(0, 6);
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((p) => (
          <Link key={p.id} to={`/parts/${p.id}`} data-testid={`${testid}-item`}
            className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded-md border border-slate-200 hover:border-[#E0781A] hover:bg-orange-50 transition-colors">
            {p.short_code || p.tag_code || p.name}
            {p.age_days != null && <span className="text-slate-400">· {p.age_days}d</span>}
            <ExternalLink className="w-3 h-3 text-slate-400" />
          </Link>
        ))}
      </div>
      {items.length > 6 && (
        <button onClick={() => setOpen((v) => !v)} className="text-xs text-[#E0781A] mt-2 inline-flex items-center gap-1">
          {open ? <>Show less <ChevronUp className="w-3 h-3" /></> : <>Show all {items.length} <ChevronDown className="w-3 h-3" /></>}
        </button>
      )}
    </div>
  );
};

// ============================================================ PULSE
function PulseView() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/inventory/yard-metrics/pulse").then(({ data }) => setD(data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);
  if (err) return <div className="text-sm text-red-500 font-mono">{err}</div>;
  if (!d) return <div className="text-sm text-slate-400 font-mono py-10 text-center">Loading pulse…</div>;
  const np = d.net_position, na = d.needs_attention, its = d.intake_to_shelf, dc = d.data_completeness;
  return (
    <div data-testid="pulse-view" className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* NET POSITION (cost only) */}
      {d.can_cost && np && (
        <Card title="Net Position" icon={np.in_black ? TrendingUp : TrendingDown} testid="pulse-net-position"
          accent={np.in_black ? "text-emerald-600" : "text-[#B01E28]"}>
          <div className={`text-4xl font-display font-black ${np.in_black ? "text-emerald-600" : "text-[#B01E28]"}`}>
            {np.net >= 0 ? "+" : "−"}{money(Math.abs(np.net))}
          </div>
          <p className="text-sm text-slate-500 mt-1">{money(np.value_extracted)} extracted − {money(np.invested)} invested</p>
          <span className={`inline-block mt-2 text-xs font-bold uppercase px-2 py-0.5 rounded ${np.in_black ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-[#B01E28]"}`}>
            {np.in_black ? "In the black" : "Recovering"}
          </span>
          <Coverage label={np.coverage_pct != null ? `Valued coverage: ${np.coverage_pct}% — ${np.coverage_label}` : np.coverage_label} testid="pulse-net-coverage" />
        </Card>
      )}

      {/* NEEDS ATTENTION */}
      {na && (
        <Card title="Needs Attention" icon={AlertTriangle} testid="pulse-needs-attention" accent="text-[#E0781A]">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-display font-black text-slate-900">{na.count}</span>
            <span className="text-sm text-slate-500">items costing you</span>
          </div>
          <div className="flex gap-3 text-xs text-slate-500 mt-1 mb-3">
            <span>{na.stuck_staging} stuck in staging</span><span>·</span><span>{na.sell_candidates} sell-candidates</span>
          </div>
          <PartLinks items={na.items} testid="pulse-attention-list" empty="All clear — nothing flagged." />
        </Card>
      )}

      {/* INTAKE-TO-SHELF */}
      {its && (
        <Card title="Intake-to-Shelf" icon={Clock} testid="pulse-intake" accent={its.flag_slow ? "text-[#B01E28]" : "text-emerald-600"}>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-display font-black text-slate-900">{its.avg_days != null ? its.avg_days : "—"}</span>
            <span className="text-sm text-slate-500">avg days yard → available</span>
          </div>
          {its.flag_slow && <span className="inline-flex items-center gap-1 text-xs font-bold text-[#B01E28] mt-2"><TrendingDown className="w-3 h-3" /> Trending slow</span>}
          <Coverage label={its.coverage_label} testid="pulse-intake-coverage" />
        </Card>
      )}

      {/* DATA COMPLETENESS */}
      {dc && (
        <Card title="Data Completeness" icon={CheckCircle2} testid="pulse-completeness" accent="text-sky-600">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-display font-black text-slate-900">{dc.pct != null ? `${dc.pct}%` : "—"}</span>
            <span className="text-sm text-slate-500">fully recorded</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2 mt-3">
            <div className="bg-sky-500 h-2 rounded-full" style={{ width: `${dc.pct || 0}%` }} />
          </div>
          <Coverage label={`${dc.complete} of ${dc.total_available} available parts are priced + labeled + photographed`} testid="pulse-completeness-coverage" />
        </Card>
      )}
    </div>
  );
}

// ============================================================ FINANCIAL
function Sparkline({ trend }) {
  if (!trend?.length) return <p className="text-sm text-slate-400">Trend will build as parts are extracted over time — nothing to plot yet.</p>;
  const vals = trend.map((t) => t.cumulative);
  const max = Math.max(...vals, 1), min = Math.min(...vals, 0);
  const W = 600, H = 90;
  const pts = trend.map((t, i) => {
    const x = trend.length === 1 ? W / 2 : (i / (trend.length - 1)) * W;
    const y = H - ((t.cumulative - min) / (max - min || 1)) * (H - 10) - 5;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <div data-testid="fin-trend">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24">
        <polyline points={pts.join(" ")} fill="none" stroke="#E0781A" strokeWidth="2.5" />
        {trend.map((t, i) => {
          const [x, y] = pts[i].split(",");
          return <circle key={i} cx={x} cy={y} r="3" fill="#E0781A" />;
        })}
      </svg>
      <div className="flex justify-between text-[11px] text-slate-400 font-mono mt-1">
        <span>{trend[0].week}</span><span>Cumulative extracted: {money(trend[trend.length - 1].cumulative)}</span><span>{trend[trend.length - 1].week}</span>
      </div>
      <Coverage label="Plotted only from real extraction timestamps — no history is fabricated." testid="fin-trend-coverage" />
    </div>
  );
}

const VBS_META = {
  ai_estimate: { label: "AI estimate", cls: "bg-indigo-50 text-indigo-800", bar: "bg-indigo-400", tone: "soft" },
  staff_estimate: { label: "Staff estimate", cls: "bg-blue-50 text-blue-800", bar: "bg-blue-400", tone: "soft" },
  market_perplexity: { label: "Market · Perplexity", cls: "bg-sky-50 text-sky-800", bar: "bg-sky-400", tone: "soft" },
  researched: { label: "Researched", cls: "bg-emerald-50 text-emerald-800", bar: "bg-emerald-500", tone: "solid" },
  not_priced: { label: "Not priced / No basis yet", cls: "bg-amber-50 text-amber-800", bar: "bg-amber-300", tone: "todo" },
};

function ValueBySource({ vbs }) {
  const [drill, setDrill] = useBackState("drill", null);   // source key
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  if (!vbs) return null;
  const total = vbs.total || 0;
  const open = async (src) => {
    if (drill === src) { setDrill(null); return; }
    setDrill(src); setItems([]); setLoading(true);
    try { const { data } = await api.get("/inventory/yard-metrics/value-by-source", { params: { source: src } }); setItems(data.items || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Card title="Internal Value by Confidence" icon={Gauge} testid="fin-value-by-source" accent="text-[#B01E28]"
      right={<span className="text-[11px] text-slate-400 font-mono">solid vs guessed</span>}>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="text-2xl font-display font-black text-slate-900">{money(total)}</span>
        <span className="text-sm text-slate-500">on-hand internal value</span>
        {vbs.soft_pct != null && <span className="ml-auto text-xs font-bold text-[#B01E28]">{vbs.soft_pct}% soft / guessed</span>}
      </div>
      {/* Review the soft pile — turns the number into an action queue */}
      {(vbs.soft || 0) > 0 && (
        <button data-testid="vbs-review-soft" onClick={() => open("soft")}
          className={`w-full mb-3 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${drill === "soft" ? "bg-[#B01E28] text-white border-[#B01E28]" : "bg-white text-[#B01E28] border-red-200 hover:bg-red-50"}`}>
          <FlaskConical className="w-3.5 h-3.5" />
          {drill === "soft" ? "Hide the soft pile" : "Review the soft pile — walk through & firm up"}
        </button>
      )}
      {drill === "soft" && (
        <div className="mb-3" data-testid="vbs-drill-soft">
          {loading ? <span className="text-xs text-slate-400 font-mono">loading…</span>
            : <PartLinks items={items} testid="vbs-drill-soft-list" empty="Nothing soft — everything's firmed up." />}
        </div>
      )}
      {/* stacked bar */}
      <div className="flex w-full h-3 rounded-full overflow-hidden bg-slate-100 mb-4" data-testid="vbs-bar">
        {vbs.buckets.map((b) => total > 0 && b.value > 0 ? (
          <div key={b.source} className={VBS_META[b.source]?.bar || "bg-slate-300"} style={{ width: `${(b.value / total) * 100}%` }} title={VBS_META[b.source]?.label} />
        ) : null)}
      </div>
      <div className="space-y-1.5">
        {vbs.buckets.map((b) => {
          const m = VBS_META[b.source] || { label: b.source, cls: "bg-slate-100 text-slate-700" };
          return (
            <div key={b.source} data-testid={`vbs-row-${b.source}`}>
              <button onClick={() => open(b.source)} className="w-full flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors">
                <span className="flex items-center gap-2 text-sm">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${m.cls}`}>{m.label}</span>
                  {m.tone === "solid" ? <span className="text-[10px] font-bold uppercase text-emerald-600">solid</span>
                    : m.tone === "todo" ? <span className="text-[10px] font-bold uppercase text-amber-600">needs pricing</span>
                      : <span className="text-[10px] uppercase text-slate-400">soft</span>}
                </span>
                <span className="flex items-center gap-2 font-mono text-sm">
                  {b.source === "not_priced"
                    ? <span className="text-amber-700 font-semibold">{b.parts} part{b.parts === 1 ? "" : "s"}</span>
                    : <><span className="text-slate-800">{money(b.value)}</span><span className="text-slate-400 text-xs">· {b.parts}</span></>}
                  {drill === b.source ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                </span>
              </button>
              {drill === b.source && (
                <div className="pl-2 pb-2" data-testid={`vbs-drill-${b.source}`}>
                  {loading ? <span className="text-xs text-slate-400 font-mono">loading…</span>
                    : <PartLinks items={items} testid={`vbs-drill-${b.source}-list`} empty={b.source === "not_priced" ? "Nothing waiting — every on-hand part has a price basis." : "No parts in this tier."} />}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Coverage label="On-hand parts only. Verified-at-sale value lives in Recovery above — verified parts have left inventory." testid="vbs-coverage" />
    </Card>
  );
}

// Pricing KPI (Option A): tool SPREAD across inventory + tool ACCURACY vs the Researched
// gold standard. Self-fetches; hides silently if the viewer lacks the pricing permission.
function PricingToolAccuracy() {
  const [d, setD] = useState(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    api.get("/pricing/tool-accuracy").then(({ data }) => setD(data)).catch(() => setHidden(true));
  }, []);
  if (hidden) return null;
  if (!d) return <Card title="Pricing Tool Accuracy" icon={Sparkles} testid="pricing-tool-accuracy" accent="text-violet-600"><div className="text-sm text-slate-400 font-mono py-6 text-center">Loading…</div></Card>;

  const rungs = [
    { key: "researched", label: "Researched \u2713", cls: "bg-emerald-500" },
    { key: "staff_ai", label: "Staff \u00b7 AI assist", cls: "bg-violet-500" },
    { key: "staff_perplexity", label: "Staff \u00b7 Perplexity", cls: "bg-sky-500" },
    { key: "staff_plain", label: "Staff estimate", cls: "bg-blue-400" },
    { key: "not_priced", label: "Not priced yet", cls: "bg-amber-300" },
  ];
  const sp = d.spread || {};
  return (
    <Card title="Pricing Confidence Ladder" icon={Sparkles} testid="pricing-tool-accuracy" accent="text-violet-600"
      right={<span className="text-[11px] text-slate-400 font-mono">{d.onhand_total} on-hand</span>}>
      {/* SPREAD — share of on-hand inventory on each rung (Researched wins first) */}
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Confidence spread (share of on-hand inventory)</div>
      <div className="flex w-full h-3 rounded-full overflow-hidden bg-slate-100 mb-2" data-testid="pta-spread-bar">
        {rungs.map((r) => (sp[r.key]?.pct ? <div key={r.key} className={r.cls} style={{ width: `${sp[r.key].pct}%` }} title={`${r.label} ${sp[r.key].pct}%`} /> : null))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-4">
        {rungs.map((r) => (
          <div key={r.key} data-testid={`pta-spread-${r.key}`} className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className={`w-2.5 h-2.5 rounded-full ${r.cls}`} />{r.label}
            <span className="font-mono font-bold">{sp[r.key]?.pct ?? 0}%</span>
            <span className="text-slate-400">({sp[r.key]?.count ?? 0})</span>
          </div>
        ))}
      </div>

      {/* ACCURACY — how close each tool's number was to the Researched gold standard */}
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Tool accuracy vs Researched sign-off (±{d.tolerance_pct}%)</div>
      <div className="grid grid-cols-2 gap-3">
        {["ai", "perplexity"].map((k) => {
          const a = d.accuracy[k];
          const label = k === "ai" ? "Ask AI" : "Perplexity";
          return (
            <div key={k} data-testid={`pta-accuracy-${k}`} className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs font-bold text-slate-700 mb-1">{label}</div>
              {a.signed_off === 0 ? (
                <div className="text-[11px] text-slate-400">No sign-offs yet to compare.</div>
              ) : (
                <>
                  <div className="text-2xl font-black text-slate-800">{a.match_rate_pct}%<span className="text-[11px] font-normal text-slate-400 ml-1">matched</span></div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{a.matched}/{a.signed_off} within tolerance · avg drift {a.avg_abs_delta_pct}%</div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-400 mt-2">High match rate ⇒ the tool prices like our Researched gold standard. Persistent drift ⇒ the tool runs off.</p>
    </Card>
  );
}


// Shop-level RECYCLING REVENUE — its own line, ISOLATED from donor recovery (Option A).
// Only rendered inside the cost-walled Financial tab; self-fetches the batch stats.
function RecyclingRevenue() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/recycle-batches/stats").then(({ data }) => setD(data)).catch(() => {});
  }, []);
  if (!d || d.recycling_revenue == null) return null;
  return (
    <Card title="Recycling Revenue" icon={Recycle} testid="fin-recycling-revenue" accent="text-emerald-600"
      right={<Link to="/recycle-batches" data-testid="fin-recycling-link" className="text-[11px] text-[#E0781A] hover:underline inline-flex items-center gap-1">Batches <ExternalLink className="w-3 h-3" /></Link>}>
      <div className="text-3xl font-display font-black text-emerald-600" data-testid="fin-recycling-total">{money(d.recycling_revenue)}</div>
      <p className="text-sm text-slate-500 mt-1">{d.closed_batches} closed batch{d.closed_batches === 1 ? "" : "es"} · {d.open_batches} open</p>
      <p className="text-[11px] text-slate-400 mt-2 flex items-start gap-1"><Info className="w-3 h-3 shrink-0 mt-0.5" />A separate shop pool — bulk scrap loads. Deliberately NOT split back to any donor truck's recovery (per-donor recycling value is unknowable).</p>
    </Card>
  );
}

function FinancialView() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [sortKey, setSortKey] = useBackState("sortKey", "pct_recovered");
  const [sortDir, setSortDir] = useBackState("sortDir", "desc");
  useEffect(() => {
    api.get("/inventory/yard-metrics/financial").then(({ data }) => setD(data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);
  const rows = useMemo(() => {
    if (!d) return [];
    const arr = [...d.per_donor];
    arr.sort((a, b) => {
      const av = a[sortKey] ?? -1, bv = b[sortKey] ?? -1;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [d, sortKey, sortDir]);
  const sortBy = (k) => { if (sortKey === k) setSortDir((s) => s === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("desc"); } };
  const exportCsv = async () => {
    try {
      const { data } = await api.get("/inventory/yard-metrics/financial.csv", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = "fas_financial_recovery.csv"; a.click(); a.remove();
    } catch { toast.error("Export failed"); }
  };
  if (err) return <div className="text-sm text-red-500 font-mono">{err}</div>;
  if (!d) return <div className="text-sm text-slate-400 font-mono py-10 text-center">Loading financials…</div>;
  const t = d.topline;
  const TL = [
    { label: "Invested", val: t.total_invested, icon: DollarSign, c: "text-slate-700" },
    { label: "Avoided Cost", val: t.total_avoided_cost, icon: PiggyBank, c: "text-emerald-600" },
    { label: "Cash", val: t.total_cash, icon: Wallet, c: "text-sky-600" },
    { label: "Value Extracted", val: t.value_extracted, icon: TrendingUp, c: "text-[#E0781A]" },
    { label: "Net Position", val: t.net_position, icon: t.net_position >= 0 ? TrendingUp : TrendingDown, c: t.net_position >= 0 ? "text-emerald-600" : "text-[#B01E28]" },
  ];
  const Th = ({ k, children, right }) => (
    <th onClick={() => sortBy(k)} className={`px-3 py-2 cursor-pointer select-none hover:text-[#E0781A] ${right ? "text-right" : "text-left"}`}>
      {children}{sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
  return (
    <div data-testid="financial-view" className="space-y-4">
      <div data-testid="fin-topline" className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {TL.map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-1"><s.icon className={`w-3.5 h-3.5 ${s.c}`} />{s.label}</div>
            <div className={`text-xl font-display font-black ${s.c}`}>{money(s.val)}</div>
          </div>
        ))}
      </div>
      <Coverage label={t.coverage.coverage_pct != null ? `Valued coverage: ${t.coverage.coverage_pct}% (${t.coverage.internal_with_ref} of ${t.coverage.internal_used} internal-use parts have a documented value)` : "No internal-use parts yet"} testid="fin-topline-coverage" />

      <div data-testid="yard-balance-strip" className="rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-bold">Yard balance · two operational axes</div>
          <div className="text-[10px] text-slate-400">Shown independently — no net / margin / profit derived between them</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div data-testid="yb-recovery" className="rounded-lg border border-emerald-100 bg-white p-3">
            <div className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 mb-2"><TrendingUp className="w-3.5 h-3.5" />Donor recovery · value out</div>
            <div className="flex gap-8">
              <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Cash</div><div className="text-lg font-display font-black text-sky-600" data-testid="yb-cash">{money(t.total_cash)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Avoided</div><div className="text-lg font-display font-black text-emerald-600" data-testid="yb-avoided">{money(t.total_avoided_cost)}</div></div>
            </div>
          </div>
          <div data-testid="yb-builder" className="rounded-lg border border-indigo-100 bg-white p-3">
            <div className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-700 mb-2"><Wrench className="w-3.5 h-3.5" />Builder cost-in · value in</div>
            <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Parts-cost-in · {d.builder_cost_in?.parts || 0} part{(d.builder_cost_in?.parts || 0) === 1 ? "" : "s"}</div><div className="text-lg font-display font-black text-indigo-600" data-testid="yb-builder-cost">{money(d.builder_cost_in?.value || 0)}</div></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Inventory Value on Hand" icon={Boxes} testid="fin-on-hand" accent="text-sky-600">
          <div className="text-3xl font-display font-black text-slate-900">{money(d.inventory_value_on_hand.value)}</div>
          <p className="text-sm text-slate-500 mt-1">list-price potential · {d.inventory_value_on_hand.parts} available parts</p>
        </Card>
        <Card title="Recovery Trend" icon={Activity} testid="fin-trend-card" accent="text-[#E0781A]">
          <Sparkline trend={d.recovery_trend} />
        </Card>
      </div>

      <RecyclingRevenue />

      <ValueBySource vbs={d.value_by_source} />

      <PricingToolAccuracy />


      <Card title="Per-Donor Recovery" icon={Layers} testid="fin-donor-card" right={
        <button data-testid="fin-export-csv" onClick={exportCsv} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#1F1F1F] hover:bg-black text-white text-xs font-medium">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>}>
        <div className="overflow-x-auto -mx-2">
          <table data-testid="fin-donor-table" className="w-full text-sm min-w-[760px]">
            <thead><tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <Th k="stock_number">Donor</Th>
              <Th k="value_extracted" right>Recovered</Th>
              <Th k="remaining_to_recover" right>Remaining</Th>
              <Th k="pct_recovered" right>% Recovered</Th>
              <Th k="acquisition_age_days" right>Age (days)</Th>
              <th className="px-3 py-2 text-center">Status</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.donor_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <Link to={`/donors/${r.donor_id}`} className="text-[#E0781A] hover:underline font-mono text-xs">{r.stock_number}</Link>
                    <div className="text-xs text-slate-500">{r.vehicle}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{money(r.value_extracted)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-500">{money(r.remaining_to_recover)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-14 bg-slate-100 rounded-full h-1.5 hidden sm:block"><div className={`h-1.5 rounded-full ${r.in_black ? "bg-emerald-500" : "bg-[#E0781A]"}`} style={{ width: `${Math.min(r.pct_recovered || 0, 100)}%` }} /></div>
                      <span className="font-mono">{r.pct_recovered != null ? `${r.pct_recovered}%` : "—"}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-slate-500">{r.acquisition_age_days ?? "—"}</td>
                  <td className="px-3 py-2 text-center">
                    {r.in_black ? <span className="text-xs font-bold text-emerald-600">In black</span> : <span className="text-xs text-slate-400">Recovering</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Per-Site Financials" icon={MapPin} testid="fin-per-site">
          <div className="space-y-2">
            {d.per_site.map((s, i) => (
              <div key={`${s.site}-${i}`} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                <span className="font-medium text-slate-700">{s.site}</span>
                <span className="font-mono text-xs text-slate-500">{money(s.value_extracted)} / {money(s.invested)} · net <b className={s.net >= 0 ? "text-emerald-600" : "text-[#B01E28]"}>{money(s.net)}</b></span>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Legacy Bucket" icon={Archive} testid="fin-legacy" accent="text-violet-600">
          <div className="text-sm text-slate-600">Pre-system parts (no acquisition cost):</div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-center">
            <div><div className="text-lg font-display font-black text-emerald-600">{money(d.legacy.avoided_cost)}</div><div className="text-[10px] uppercase text-slate-400">Avoided</div></div>
            <div><div className="text-lg font-display font-black text-sky-600">{money(d.legacy.cash)}</div><div className="text-[10px] uppercase text-slate-400">Cash</div></div>
            <div><div className="text-lg font-display font-black text-slate-700">{d.legacy.parts_count}</div><div className="text-[10px] uppercase text-slate-400">Parts</div></div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ============================================================ OPERATIONS
function OpsView() {
  const [win, setWin] = useBackState("win", 30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null);
    api.get(`/inventory/yard-metrics/operations?window_days=${win}&stuck_days=14`).then(({ data }) => setD(data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail)));
  }, [win]);
  if (err) return <div className="text-sm text-red-500 font-mono">{err}</div>;
  const dq = d?.data_quality;
  const DQ = dq && [
    { k: "unlabeled", label: "Unlabeled", icon: Tag },
    { k: "unidentified", label: "Unidentified", icon: HelpCircle },
    { k: "unpriced", label: "Unpriced", icon: DollarSign },
    { k: "unphotographed", label: "No photos", icon: Camera },
  ];
  return (
    <div data-testid="operations-view" className="space-y-4">
      {/* HIS WHOLE-SHOP NAMING TOTALS, on the report screen as well as Catch-up tools (his call, 24 Oct) */}
      <TagNamingReport />
      <div className="flex items-center gap-2 flex-wrap" data-testid="ops-window">
        <span className="text-xs uppercase font-bold text-slate-500">Window:</span>
        {OPS_WINDOWS.map(([v, l]) => (
          <button key={v} onClick={() => setWin(v)} data-testid={`ops-window-${v}`}
            className={`text-xs font-medium px-3 h-8 rounded-lg border transition-colors ${win === v ? "bg-[#1F1F1F] text-white border-[#1F1F1F]" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      {!d ? <div className="text-sm text-slate-400 font-mono py-10 text-center">Loading operations…</div> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card title="Intake-to-Shelf" icon={Clock} testid="ops-intake" accent={d.intake_to_shelf.flag_slow ? "text-[#B01E28]" : "text-emerald-600"}>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-display font-black text-slate-900">{d.intake_to_shelf.avg_days ?? "—"}</span>
                <span className="text-sm text-slate-500">avg days · {d.intake_to_shelf.stuck_count} stuck &gt;14d</span>
              </div>
              <Coverage label={d.intake_to_shelf.coverage_label} testid="ops-intake-coverage" />
              <div className="mt-3"><PartLinks items={d.intake_to_shelf.stuck_items} testid="ops-intake-stuck" empty="Nothing stuck — flowing well." /></div>
            </Card>
            <Card title="Staging Backlog" icon={Layers} testid="ops-staging" accent="text-[#E0781A]">
              <div className="text-3xl font-display font-black text-slate-900">{d.staging_backlog.total}</div>
              <div className="flex gap-3 text-xs text-slate-500 mt-1 mb-3">
                <span>ID: {d.staging_backlog.by_status.staging_needs_id || 0}</span>
                <span>Grade: {d.staging_backlog.by_status.staging_needs_grade || 0}</span>
                <span>Price: {d.staging_backlog.by_status.staging_needs_price || 0}</span>
              </div>
              <PartLinks items={d.staging_backlog.items} testid="ops-staging-list" empty="No backlog." />
            </Card>
          </div>

          <Card title="Processing Throughput" icon={Gauge} testid="ops-throughput" accent="text-sky-600">
            <div className="text-3xl font-display font-black text-slate-900">{d.throughput.total}<span className="text-sm font-normal text-slate-500"> parts → available in window</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
              <div>
                <div className="text-[11px] uppercase font-bold text-slate-400 mb-1">By site</div>
                {d.throughput.by_site.map((s, i) => <div key={`${s.site}-${i}`} className="flex justify-between text-sm border-b border-slate-100 py-1"><span>{s.site}</span><span className="font-mono">{s.count}</span></div>)}
              </div>
              <div>
                <div className="text-[11px] uppercase font-bold text-slate-400 mb-1">By user</div>
                {d.throughput.by_user.slice(0, 6).map((u) => <div key={u.user} className="flex justify-between text-sm border-b border-slate-100 py-1"><span className="font-mono text-xs truncate">{u.user}</span><span className="font-mono">{u.count}</span></div>)}
              </div>
            </div>
          </Card>

          <Card title="Data Quality — click to fix" icon={CheckCircle2} testid="ops-dataquality" accent="text-amber-600">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {DQ.map(({ k, label, icon: I }) => (
                <div key={k} className="border border-slate-100 rounded-lg p-3" data-testid={`ops-dq-${k}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700"><I className="w-3.5 h-3.5 text-amber-600" />{label}</span>
                    <span className="font-mono text-sm"><b>{dq[k].count}</b> <span className="text-slate-400">({dq[k].pct}%)</span></span>
                  </div>
                  <PartLinks items={dq[k].items} testid={`ops-dq-${k}-list`} empty="All good." />
                </div>
              ))}
            </div>
            <Coverage label={`${dq.total_active} active parts (available + staging) assessed`} testid="ops-dq-coverage" />
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Aging & Dead Stock" icon={Clock} testid="ops-aging" accent="text-[#B01E28]">
              <p className="text-sm text-slate-500 mb-2">Sitting longest without moving · <b>{d.dead_stock.count}</b> dead-stock</p>
              <PartLinks items={d.aging} testid="ops-aging-list" empty="No stale stock." />
            </Card>
            <Card title="Overstock / Slow-Movers" icon={Boxes} testid="ops-overstock" accent="text-[#E0781A]">
              {d.overstock.length ? d.overstock.map((g, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5">
                  <span className="truncate">{g.label}</span>
                  <span className="font-mono text-xs">{g.on_hand} on hand</span>
                </div>
              )) : <p className="text-sm text-slate-400">No high-quantity duplicates (≥5).</p>}
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card title="Returns & Write-offs" icon={RotateCcw} testid="ops-returns" accent="text-violet-600">
              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                <div><div className="text-2xl font-display font-black text-slate-700">{d.returns_writeoffs.returned}</div><div className="text-[10px] uppercase text-slate-400">Returned</div></div>
                <div><div className="text-2xl font-display font-black text-[#B01E28]">{d.returns_writeoffs.scrapped}</div><div className="text-[10px] uppercase text-slate-400">Scrapped</div></div>
                <div><div className="text-2xl font-display font-black text-amber-600">{d.returns_writeoffs.recovery_reversed}</div><div className="text-[10px] uppercase text-slate-400">Reversed</div></div>
              </div>
              <div className="text-[11px] uppercase font-bold text-slate-400 mb-1">Top scrap reasons</div>
              {d.returns_writeoffs.top_reasons.length ? d.returns_writeoffs.top_reasons.map((r) => (
                <div key={r.reason} className="flex justify-between text-sm py-0.5"><span className="flex items-center gap-1"><Trash2 className="w-3 h-3 text-slate-400" />{r.reason}</span><span className="font-mono">{r.count}</span></div>
              )) : <p className="text-sm text-slate-400">None this period.</p>}
            </Card>
            <Card title="Per-Site Stock" icon={MapPin} testid="ops-sitestock" accent="text-sky-600">
              {d.per_site_stock.sites.map((s, i) => <div key={`${s.site}-${i}`} className="flex justify-between text-sm border-b border-slate-100 py-1.5"><span>{s.site}</span><span className="font-mono">{s.count}</span></div>)}
              <div className="flex justify-between text-sm py-1.5 text-[#E0781A]"><span>In transit</span><span className="font-mono">{d.per_site_stock.in_transit}</span></div>
            </Card>
            <Card title="Legacy Intake" icon={Archive} testid="ops-legacy" accent="text-violet-600">
              <div className="text-3xl font-display font-black text-slate-900">{d.legacy_intake.count}</div>
              <p className="text-sm text-slate-500 mt-1 mb-2">checked in this window</p>
              <PartLinks items={d.legacy_intake.items} testid="ops-legacy-list" empty="No legacy intake this window." />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================ MAIN
export default function YardProductionPage() {
  const { has } = useAuth();
  const canCost = has("view_acquisition_cost");
  const canOps = has("view_operations_metrics");
  const [tab, setTab] = useBackState("tab", "pulse");
  const tabs = [
    { id: "pulse", label: "Pulse", icon: Activity, show: true },
    { id: "financial", label: "Financial", icon: DollarSign, show: canCost, locked: !canCost },
    { id: "operations", label: "Operations", icon: Gauge, show: canOps },
  ].filter((t) => t.show);

  return (
    <div data-testid="yard-metrics-page" className="space-y-5">
      <SyntheticDataBanner testid="yard-synthetic-banner" />
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900">Yard Metrics</h1>
        <p className="text-sm text-slate-500 mt-1">Inventory & cash-flow visibility — at a glance, with depth one click away.</p>
      </div>

      <div data-testid="yard-tabs" className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? "border-[#E0781A] text-[#B01E28]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <t.icon className="w-4 h-4" />{t.label}
            {t.id === "financial" && <Lock className="w-3 h-3 text-slate-400" />}
          </button>
        ))}
      </div>

      {tab === "pulse" && <PulseView />}
      {tab === "financial" && canCost && <FinancialView />}
      {tab === "operations" && canOps && <OpsView />}
    </div>
  );
}
